/* globals showModalConfirm, showModalError */
import React, { useContext, useEffect, useState } from "react";
import SettingsView from "common/SettingsView";
import styled from "styled-components";
import BqnContext from "common/BqnContext";
import Request from "common/Request";
import ActionsContext from "common/ActionsContext";
import { Header } from "common/Header";
import Modal from "common/Modal";
import { ShowManagementBypassSettings } from "views/Config/Interfaces/Bypass/component";
import { getBqnIdCmdPart } from "common/api";
import Help from "./Help";
import Table from "common/graphs/Table/Table";

const CANT_PROCEED_ERROR = `Cannot proceed while there are pending config changes. Try again later.`;

const clear = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

class UserDeclined extends Error {}

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  padding-top: 5px;
`;

const checkNothingPending = () =>
  ifCl.run("show config diff").then((response) => {
    if (response.length > 0) {
      throw new Error(CANT_PROCEED_ERROR);
    }
  });

const submitCommand = (command) => ifCl.run(command + "\n", /*is batch*/ true);

const clearConfigAttempt = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

const composeApplyCommand = (originalSettings, settings) => {  
  if (settings.mode === "disabled") {
    return `configure
high-availability
hid normal
hid bypass bqn-id ${settings.bqn}
hid normal bqn-id ${settings.bqn}
no hid normal
no hid bypass bqn-id ${settings.bqn}
no hid normal bqn-id ${settings.bqn}
commit
`;
  } else if (settings.mode === "bypass") {
    return `configure
high-availability
hid normal
hid bypass bqn-id ${settings.bqn}
hid normal bqn-id ${settings.bqn}
no hid normal
no hid normal bqn-id ${settings.bqn}
commit
`;
  } else if (settings.mode === "normal") {
    return `configure
high-availability
hid normal
hid bypass bqn-id ${settings.bqn}
hid normal bqn-id ${settings.bqn}
no hid normal
no hid bypass bqn-id ${settings.bqn}
commit
`;
  }

  return null;
};

const notifyAndRevert = (error) => {
  if (error instanceof UserDeclined) {
    return { error: null };
  }
  error = error.message === undefined ? new Error(error) : error;
  clearConfigAttempt();
  showModalError("Error", error);
};

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  whenDone,
}) => {
  const handleApply = () => {
    const cmds = items.map((item) =>
      composeApplyCommand({}, { mode: "disabled", bqn: item.bqn })
    );
    ifCl
      .run(`${cmds.join("\n")}\n`, /*is batch*/ true)
      .then(() => {
        setShowDeleteModal(false);
        whenDone();
      })
      .catch((error) => showModalError("Error", error.message || error));
  };
  if (!showDeleteModal) {
    return null;
  }
  const title =
    items.length === 1
      ? `Disable bypass device${
          items[0].bqn === "all" || !items[0].bqn
            ? ""
            : ` in BQN ${items[0].bqn}`
        }?`
      : `Disable ${items.length} bypass devices?`;
  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={items.length > 1 && items.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {items.length === 1 ? (
            <p><b>WARNING</b>: The bypass device will be disabled. All configuration about the device will be lost and the mode of the device will be undetermined.</p>
          ) : null}
          {items.length > 1 ? (
            <>
              {items.length < 100 ? (
                <ul>{items.map((item, i) => (<li key={`${item.device}-${i}`}>{item.device} {item.bqn && item.bqn !== "all" ? `(BQN ${item.bqn})` : ""}</li>))}</ul>
              ) : null}
              <p><b>WARNING</b>: The bypass devices will be disabled. All configuration about the devices will be lost and the mode of the devices will be undetermined.</p>
            </>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      onApply={handleApply}
    />
  );
};

const ContextMenu = () => {
  const actions = useContext(ActionsContext);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <a
        data-loading-effect="pulse"
        data-toggle="cardloading"
        title="Help"
        onClick={() => actions.send("open-help")}
      >
        <i className="material-icons">help_outline</i>
      </a>
      <a
        onClick={() => actions.send("do-load")}
        data-toggle="cardloading"
        data-loading-effect="pulse"
        title="Refresh"
      >
        <i className="material-icons">refresh</i>
      </a>
    </ul>
  );
};

const Bypass = () => {
  const [request, setRequest] = useState(null);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const actions = useContext(ActionsContext);

  useEffect(() => {
    doLoad();
    const unsubscribe = actions.recv("do-load", doLoad);
    return unsubscribe;
  }, []);

  const doLoad = () => {
    setRequest(
      ifCl.run("show high-availability hid").then((response) => {
        return { response };
      })
    );
  };

  const apply = (settings, originalSettings = {}) =>
    checkNothingPending()
      .then(() => composeApplyCommand(originalSettings, settings))
      .then(submitCommand)
      .then(() => doLoad())
      .catch(notifyAndRevert);

  const getColumns = () => {
    return [
      ...(mode !== "standalone"
        ? [
            {
              label: "BQN",
              id: "BQN",
              numeric: true,
            },
          ]
        : []),
      {
        label: "MODEL",
        id: "MODEL",
        numeric: false,
      },
      {
        label: "SERIAL-NUMBER",
        id: "SERIAL-NUMBER",
        numeric: false,
      },
      {
        label: "MODE",
        id: "MODE",
        numeric: false,
      },
      {
        label: "LINKS-IN-BYPASS",
        id: "LINKS-IN-BYPASS",
        numeric: false,
      },
      {
        label: "ACTIONS",
        id: "ACTIONS",
        sort: false,
        actions: [
          {
            label: "Edit",
            icon: "edit",
            onClick: (row) => {
              setEditing({
                device: row["MODEL"],
                bqn: mode === "cluster" ? row["BQN"] : bqnId,
                mode: row["MODE"],
              });
            },
          },
          {
            label: "Delete",
            icon: "delete",
            onClick: (row) => {
              setDeleteSelected([
                {
                  device: row["MODEL"],
                  bqn: mode === "cluster" ? row["BQN"] : bqnId,
                },
              ]);
              setShowDeleteModal(true);
            },
          },
        ],
      },
    ];
  };

  const columns = getColumns();

  return (
    <>
      <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
        <div className="card container-lg">
          <Header
            title="External Bypass Devices"
            contextMenu={<ContextMenu />}
            bqnSelector={true}
          />
          <div className="body flex-panel-column expect-overflow">
            <TableContainer>
              <Request during={request}>
                {({ response }) => {
                  const [header, ...rows] = response.trim().split("\n");
                  const headerAttrs = header.trim().split(/\s+/);
                  const newRows = rows.map((row) => {
                    const rowAttrs = row.trim(/\s/).split(/\s+/);
                    const newEntry = rowAttrs.reduce((acc, attr, index) => {
                      acc[headerAttrs[index]] = attr;
                      return acc;
                    }, {});
                    return newEntry;
                  });
                  const filteredRows = newRows
                    .filter((row) => {
                      if (mode === "standalone" || bqnId === "all") {
                        return true;
                      }
                      return parseFloat(row["BQN"]) === parseFloat(bqnId);
                    })
                    .map((row, index) => ({
                      ...row,
                      id: row.id !== undefined ? row.id : index,
                    }));

                  return (
                    <Table
                      isSelectable={true}
                      isRowSelectable={false}
                      data={filteredRows}
                      sort={true}
                      columns={columns}
                      search={true}
                      csvPrefix="external-bypass-devices"
                      rowsPerPageNr={50}
                      selected={selected}
                      setSelected={setSelected}
                      deleteSelected={(values) => {
                        const selectedItems = filteredRows
                          .filter((row) => values.includes(row.id))
                          .map((row) => ({
                            device: row["MODEL"],
                            bqn: mode === "cluster" ? row["BQN"] : bqnId,
                          }));
                        setDeleteSelected(selectedItems);
                        setShowDeleteModal(true);
                      }}
                    />
                  );
                }}
              </Request>
            </TableContainer>
          </div>
        </div>
      </div>
      {editing !== null && (
        <Modal
          id="bypass-edit"
          large={true}
          onClose={() => setEditing(null)}
          noPadding={true}
          content={() => (
            <SettingsView
              className="container-lg"
              load={() =>
                Promise.resolve({
                  bqn: editing.bqn,
                  device: editing.device,
                  mode: editing.mode,
                })
              }
              apply={apply}
              clear={clear}
              ContextMenu={() => null}
              title={`External Bypass Device ${editing.device}${
                mode === "cluster" ? ` in BQN ${editing.bqn}` : ""
              }`}
              applyActionClassName="hidden-to-operators"
              wrapIntoForm={false}
              subtitle={
                <small>
                  Press <code>Apply</code> button at the end of the page to set
                  the new configuration values.
                </small>
              }
              bqnSelector={false}
              noMargin={true}
            >
              <ShowManagementBypassSettings />
            </SettingsView>
          )}
        />
      )}
      <DeleteModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        items={deleteSelected}
        whenDone={() => {
          setSelected([]);
          doLoad();
        }}
      />
      <Help />
    </>
  );
};

export default Bypass;
